import React, { useEffect } from 'react';
import { X } from 'lucide-react';

const overlay = { background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(10px)' };
const panel = { background: '#141414', border: '1px solid rgba(201,168,76,0.18)' };
const h2 = { fontSize: '20px', fontWeight: 700, color: '#F5F5F0', marginBottom: '10px' };
const p = { fontSize: '14px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.8, marginBottom: '10px' };
const li = { fontSize: '14px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.7 };
const eyebrow = { fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase', color: '#C9A84C', fontWeight: 600 };
const strong = { color: '#F5F5F0' };

const Section = ({ n, title, children }) => (
  <section className="mb-8">
    <h2 className="font-display" style={h2}>{n}. {title}</h2>
    <div className="font-body">{children}</div>
  </section>
);

const PrivacyPolicy = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" style={overlay} data-testid="privacy-modal">
      <div className="min-h-screen py-10 px-4">
        <div className="mx-auto max-w-3xl">
          <div className="flex justify-between items-start mb-8">
            <div>
              <span className="font-body" style={eyebrow}>Legal</span>
              <h1 className="font-display mt-2" style={{ fontSize: 'clamp(28px,4vw,40px)', fontWeight: 700, color: '#F5F5F0' }}>Política de Privacidad</h1>
              <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>Yamauchi Ramen · Última actualización: 8 de agosto de 2026</p>
            </div>
            <button onClick={onClose} className="text-[#C2C1B6] hover:text-[#C9A84C] transition-colors p-2" data-testid="privacy-close" aria-label="Cerrar">
              <X className="w-7 h-7" />
            </button>
          </div>

          <div className="p-6 sm:p-8" style={panel}>
            <p className="font-body" style={p}>
              En Yamauchi Ramen valoramos tu privacidad. Esta política explica qué datos recopilamos cuando usas nuestro sitio web, creas una cuenta y utilizas nuestro servicio de pedidos y entrega (delivery), cómo los usamos, con quién los compartimos y cuáles son tus derechos, conforme a las leyes aplicables del Estado Libre Asociado de Puerto Rico y de los Estados Unidos.
            </p>

            <Section n="1" title="Información que recopilamos">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}><strong style={strong}>Datos de cuenta:</strong> nombre, correo electrónico y teléfono al registrarte con email y contraseña.</li>
                <li style={li}><strong style={strong}>Inicio de sesión con Google:</strong> si eliges "Continuar con Google", recibimos tu nombre, correo y foto de perfil. No recibimos tu contraseña de Google.</li>
                <li style={li}><strong style={strong}>Pedidos como invitado:</strong> nombre, teléfono y correo para procesar y entregar tu pedido y enviarte el recibo.</li>
                <li style={li}><strong style={strong}>Datos del pedido y entrega:</strong> artículos, personalizaciones, lugar de entrega seleccionado, notas e historial de pedidos.</li>
                <li style={li}><strong style={strong}>Datos de pago:</strong> el pago lo procesa Stripe. <strong style={strong}>No almacenamos los datos completos de tu tarjeta;</strong> solo el estado y el monto de la transacción.</li>
                <li style={li}><strong style={strong}>Preferencias de comunicación:</strong> si aceptas o no recibir ofertas y novedades por email.</li>
                <li style={li}><strong style={strong}>Datos de uso:</strong> información básica de navegación e idioma para el funcionamiento del sitio.</li>
                <li style={li}><strong style={strong}>Dirección IP:</strong> registramos tu dirección IP con fines de seguridad, prevención de fraude, límites de uso y protección contra abuso del servicio (por ejemplo, en los registros de acceso e intentos de inicio de sesión).</li>
              </ul>
            </Section>

            <Section n="2" title="Cómo usamos tu información">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Crear y administrar tu cuenta e inicio de sesión.</li>
                <li style={li}>Procesar tus pedidos y pagos, y coordinar la entrega.</li>
                <li style={li}><strong style={strong}>Enviarte correos transaccionales:</strong> recibo/confirmación de tu pedido y avisos de estado (por ejemplo, "tu pedido va en camino").</li>
                <li style={li}><strong style={strong}>Enviarte ofertas y novedades</strong> por email, únicamente si diste tu consentimiento.</li>
                <li style={li}>Generar la ruta de entrega y brindarte seguimiento del pedido.</li>
                <li style={li}>Seguridad, prevención de fraude y cumplimiento legal y contable.</li>
              </ul>
            </Section>

            <Section n="3" title="Lugar de entrega">
              <p style={p}>
                Para realizar un pedido, seleccionas tu <strong style={strong}>lugar de entrega desde una lista predefinida</strong> de puntos autorizados (instituciones y lugares dentro de nuestra área de servicio en Cayey). No recopilamos ni rastreamos de forma continua la ubicación GPS de tu dispositivo. Durante la entrega, nuestro personal de reparto puede compartir su propia ubicación para ofrecerte seguimiento en tiempo real; esa ubicación es del repartidor, no tuya.
              </p>
            </Section>

            <Section n="4" title="Pagos (Stripe)">
              <p style={p}>
                Los pagos se procesan de forma segura mediante Stripe, Inc. Al pagar, tus datos de tarjeta se envían directamente a Stripe y se rigen por su política de privacidad. Yamauchi Ramen no almacena los datos completos de tu tarjeta.
              </p>
            </Section>

            <Section n="5" title="Inicio de sesión con Google">
              <p style={p}>
                Ofrecemos autenticación con Google como método opcional. Al usarlo, Google comparte con nosotros tu nombre, correo y foto de perfil para crear o acceder a tu cuenta. Este proceso se rige también por la Política de Privacidad y los Términos de Google.
              </p>
            </Section>

            <Section n="6" title="Comunicaciones por email">
              <p style={p}>
                Utilizamos <strong style={strong}>Resend</strong> como proveedor para el envío de nuestros correos.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}><strong style={strong}>Correos transaccionales:</strong> recibo/confirmación de pedido y avisos de estado. Son necesarios para el servicio y se envían cuando realizas un pedido.</li>
                <li style={li}><strong style={strong}>Correos de marketing (ofertas y novedades):</strong> solo se envían si diste tu consentimiento (al registrarte o desde tu perfil). Puedes <strong style={strong}>darte de baja</strong> en cualquier momento desde tu perfil, respondiendo al correo, o escribiéndonos a yamauchi.cayey@gmail.com.</li>
              </ul>
            </Section>

            <Section n="7" title="Con quién compartimos tu información">
              <p style={p}>No vendemos tu información personal. La compartimos únicamente con:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}><strong style={strong}>Stripe</strong> — para procesar pagos.</li>
                <li style={li}><strong style={strong}>Google</strong> — si eliges iniciar sesión con Google.</li>
                <li style={li}><strong style={strong}>Resend</strong> — para el envío de correos (recibos, avisos y ofertas).</li>
                <li style={li}><strong style={strong}>Nuestro personal de entrega</strong> — recibe tu nombre, lugar de entrega y teléfono únicamente para completar la entrega.</li>
                <li style={li}><strong style={strong}>Proveedores de infraestructura</strong> — que alojan la aplicación bajo obligaciones de confidencialidad.</li>
                <li style={li}>Autoridades, cuando la ley lo requiera.</li>
              </ul>
            </Section>

            <Section n="8" title="Cookies y almacenamiento local">
              <p style={p}>
                Usamos almacenamiento local del navegador para mantener tu sesión iniciada (token de acceso) y recordar tus preferencias (idioma, carrito). Puedes borrarlos desde tu navegador; al hacerlo se cerrará tu sesión.
              </p>
            </Section>

            <Section n="9" title="Retención de datos">
              <p style={p}>
                Conservamos tus datos mientras tu cuenta esté activa y durante el tiempo necesario para cumplir obligaciones legales, fiscales y de resolución de disputas. Puedes solicitar la eliminación de tu cuenta y datos en cualquier momento.
              </p>
            </Section>

            <Section n="10" title="Tus derechos">
              <ul className="list-disc pl-5 space-y-2">
                <li style={li}>Acceder a la información personal que tenemos sobre ti.</li>
                <li style={li}>Corregir datos inexactos.</li>
                <li style={li}>Solicitar la eliminación de tu cuenta y datos.</li>
                <li style={li}><strong style={strong}>Darte de baja de los correos de marketing</strong> en cualquier momento (desde tu perfil o escribiéndonos).</li>
              </ul>
              <p style={{ ...p, marginTop: '10px' }}>Para ejercer estos derechos, escríbenos a yamauchi.cayey@gmail.com.</p>
            </Section>

            <Section n="11" title="Seguridad">
              <p style={p}>
                Implementamos medidas razonables (conexiones cifradas HTTPS, contraseñas protegidas con hash, acceso restringido) para proteger tu información. Ningún sistema es 100% infalible, pero trabajamos para mantener tus datos seguros.
              </p>
            </Section>

            <Section n="12" title="Menores de edad">
              <p style={p}>
                Nuestros servicios están dirigidos a personas mayores de 18 años. No recopilamos intencionalmente datos de menores. La venta de bebidas alcohólicas (solo disponible en el local) está restringida a mayores de 18 años con identificación válida; no se venden bebidas alcohólicas a través del servicio de entrega.
              </p>
            </Section>

            <Section n="13" title="Cambios a esta política">
              <p style={p}>
                Podemos actualizar esta política ocasionalmente. Publicaremos la versión vigente en el sitio y, ante cambios significativos, lo notificaremos por este medio o nuestras redes sociales.
              </p>
            </Section>

            <Section n="14" title="Contacto">
              <div className="p-4" style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.14)' }}>
                <p style={{ ...p, marginBottom: '4px', color: '#F5F5F0', fontWeight: 500 }}>Yamauchi Ramen</p>
                <p style={{ ...p, marginBottom: '2px' }}>Urb. C1 Cll 14, Cayey, PR 00633</p>
                <p style={{ ...p, marginBottom: '2px' }}>Tel: +1 (939) 593-3353</p>
                <p style={{ ...p, marginBottom: '2px' }}>Email: yamauchi.cayey@gmail.com</p>
                <p style={{ ...p, marginBottom: '0' }}>Instagram: @yamauchiramenpr</p>
              </div>
            </Section>
          </div>

          <div className="text-center mt-8">
            <button onClick={onClose} className="font-body uppercase" style={{ border: '1px solid #C9A84C', color: '#C9A84C', padding: '13px 30px', fontSize: '12px', letterSpacing: '2px', fontWeight: 600, background: 'transparent', cursor: 'pointer' }} data-testid="privacy-close-bottom">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
